import React from 'react'
import CollectionWidgetManager from 'src/utils/collections.widget.service';
import TinyMCE from 'react-tinymce/lib/components/TinyMCE';
import { useDispatch, useSelector } from 'react-redux'
import cx from 'classnames';
import styles from './editor.module.scss';
import _ from 'lodash';
import { fetchLinkedInOrganization } from 'src/redux/actions/integrations/linkedIn'; 
import { calculateDropdownPosition, getDropdownStyle } from 'src/utils/helper';

interface IDescriptionEditor {
  content : string;
  onChange : (value : string) => void;
  widgetManager: CollectionWidgetManager;
  rephrasing?: any;
  className?: string;
  outputFormat?: string;
  tones?: any;
  placeholder?: string;
  setLimitReached: (value: boolean) => void;
}

interface IOrganisation {
  id: string;
  name: string;
  profile: string;
}

const DescriptionEditor:React.FC<IDescriptionEditor> = ({ content, onChange, widgetManager, tones, className, outputFormat = 'text', placeholder = "Type your description here...", setLimitReached }) => {
  const linkLength = 100
  const [initialized, setInitialized] = React.useState(false);
  const [showOrganizationDropdown, setshowOrganizationDropdown] = React.useState(false);
  const [organizationDropdownStyle, setorganizationDropdownStyle] = React.useState<React.CSSProperties>({});
  const searchTermRef = React.useRef<string>('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);
// Used to save the organization mention position
  const [bookmarkInfo, setMentionInfo] = React.useState(null);

  const dispatch = useDispatch();
  
  const organisations = useSelector(
    (state: any) =>
      _.get(state, "integrations.linkedIn.organisation.records", []) as IOrganisation[]
  );

  const organisationLoading = useSelector((state: any) => 
    _.get(state, "integrations.linkedIn.organisation.loading", false)
  );

  const organisationError = useSelector((state: any) => 
    _.get(state, "integrations.linkedIn.organisation.error", null)
  );
  
  const description = React.useMemo(() => {
    let processedContent = content.replace(/\n/g, "<br/>")
    processedContent = processedContent.replaceAll(`{link}`, `<span class="variable mceNonEditable" style="text-decoration:underline" contenteditable="false"  data-original-variable="{{asset.link}}"> {link} </span>`);
    return processedContent;
    }, [tones]);
    
  const variables = [
    {
      id: 'asset.link',
      name: ` {link} `,
      type: 'variable',
      getValue: (parameters) => {
        return parameters['asset.link'];
      }  
    }
  ]
const editorRef = React.useRef<any>(null);
const isLinkAppended = (content) => {
  return content.includes(`{link}`);
}

  const decodeHTMLEntities = (text) => {
    const doc = new DOMParser().parseFromString(text, "text/html");
    return doc.body.textContent || "";
  };

const validateContent = (content) => {
  const processedContent = content.replaceAll(`{link}`,'');
  let processedContentLength = processedContent.length;
  const maxLength = 2900;
  if(isLinkAppended(content)) {
    const noOfLinksAttached = content.match(/{link}/g).length;
    processedContentLength = processedContentLength + (noOfLinksAttached * linkLength)
  }
  if (processedContentLength > maxLength) {
    setLimitReached(true);
  } 
  else {
    setLimitReached(false);
  }
}

 const fetchAndshowOrganizationDropdown = (searchTerm: string) => {
  searchTermRef.current = searchTerm;
  const position = calculateDropdownPosition({ editorRef });
  
  if (position) {
    const style = getDropdownStyle(position);
    setorganizationDropdownStyle(style);

    setMentionInfo(editorRef.current.selection.getBookmark());

    dispatch(fetchLinkedInOrganization(searchTerm));
    setshowOrganizationDropdown(true);
  }
};

  const handleOrganizationClick = (org: IOrganisation) => {
    if (!editorRef.current || !bookmarkInfo) return;

    const editor = editorRef.current;

    const tinyEditor = document.getElementsByClassName('tinymce-editor')[0] as HTMLElement;

    editor.selection.moveToBookmark(bookmarkInfo);
    
    const content = tinyEditor.innerHTML;

    const searchPattern = `@${searchTermRef.current}`;
    const organizationSpan = `<span class="variable mceNonEditable" contenteditable="false" data-mce-contenteditable="false" data-original-variable="${org.id}" data-content-type="linkedcompany" style="color:#0A66C2;font-weight:500;background-color:#EDF3F8;padding:2px 4px;margin:0px 4px;border-radius:4px;cursor:default;">${org.profile}</span>`;

    const lastIndex = tinyEditor.innerHTML.lastIndexOf(searchPattern);

    if (lastIndex !== -1) {
      let endIndex = lastIndex + searchPattern.length;
      if (content[endIndex] === ' ') {
        endIndex++; 
      }

      const afterContent = content.slice(endIndex).trimStart();

      const newContent = 
        content.substring(0, lastIndex) + 
        organizationSpan +
        "<span> </span>" + 
        afterContent;
      
      tinyEditor.innerHTML = newContent;
    
      const mentionOrganizationSpans = tinyEditor.querySelectorAll(`span[data-original-variable="${org.id}"]`);
      
      if (mentionOrganizationSpans.length > 0) {
        const insertedMention = mentionOrganizationSpans[mentionOrganizationSpans.length - 1];
        
        let nextNode = insertedMention.nextSibling;

        const range = editor.dom.createRng();

        range.setStart(nextNode, 0); 
        range.setEnd(nextNode, 1);
        editor.selection.setRng(range);
        editor.selection.collapse(false);
      }
    }
    
    validateContent(content);
    setshowOrganizationDropdown(false);
    searchTermRef.current = '';
    setMentionInfo(null); 
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setshowOrganizationDropdown(false);
        setMentionInfo(null);
      }
    };

    if (showOrganizationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOrganizationDropdown]);

  return (
    <>
      {!initialized &&
        <style>
          {`
            .tox-tinymce-inline {
              display: none !important;
              opacity: 0;
            }
          `}
        </style>
      }
      
      {showOrganizationDropdown && (
        <div ref={dropdownRef} style={organizationDropdownStyle}>
          {organisationLoading ? (
            <div className={cx(styles.loading)}>
              Loading...
            </div>
          ) : organisations.length > 0 ? (
            organisations.map((org) => (
              <div
                key={org.id}
                onClick={() => handleOrganizationClick(org)}
                className={cx(styles.orgName)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                {org.profile}
              </div>
            ))
          ) : (
            <div className={cx(styles.loading)}>
              No results
            </div>
          )}
        </div>
      )}

      <TinyMCE
        content={description}
        className={cx(styles.tinyEditor,className, "tinymce-editor")}
        config={{
          placeholder: placeholder,
          inline: true,
          branding: false,
          statusbar: false,
          contextmenu: false,
          theme: 'silver',
          plugins: 'code, autoresize, noneditable, variable, paste, link, autolink, lists',
          menubar: false,
          toolbar: 'undo redo | asset_link',
          fontsize_formats: '5px 5.5px 6.5px 7.5px 8px 9px 10px 10.5px 11px 12px 14px 16px 18px 20px 22px 24px 26px 28px 36px 48px 72px',
          paste_as_text: true,
          paste_preprocess: (plugin, args) => {
            args.content = args.content
              .replace(/[“”]/g, '"')
              .replace(/[‘’]/g, "'")
              .replace(/–/g, '-')
              .replace(/—/g, '--')
              .replace(/…/g, '...')
              .replace(/\u00a0/g, ' ');
          },
          paste_retain_style_properties: "color",
          resize: false,
          height: 120,
          content_css: [
            'https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i'
          ],
          default_link_target: "_blank",
          browser_spellcheck: true,
          convert_urls: false,
          relative_urls: false,
          remove_script_host: false,
          forced_root_block: '',
          force_br_newlines: true,
          force_p_newlines: false,
          extended_valid_elements: 'span[class|contenteditable|data-original-variable|data-content-type|style],a[href|target|rel|class|contenteditable|data-original-variable|data-content-type|style]',
          custom_elements: 'span',
          valid_children: '+span[#text]',
          variable_mapper: (id) => {
            const variable = variables.find((item) => {
              return item.id == id;
            });
            if (variable) {
              return variable.name;
            }
          },
          variable_valid: ['asset.link'],
          setup: (editor) => {
            editorRef.current = editor;

            editor.on('PostRender', () => {
              setTimeout(() => {
                editor.ui.hide();
                setInitialized(true);
              }, 300);
            });
          
            editor.on('keyup', (e) => {
              validateContent(editor.getContent({ format: 'text' }));

              if (e.key === ' ' || e.keyCode === 32) {
                const selection = editor.selection;
                const range = selection.getRng();
                const node = range.startContainer;

                const fullText = node.textContent || '';
                const cursorPos = range.startOffset;
                const textBeforeCursor = fullText.substring(0, cursorPos);

                const match = textBeforeCursor.match((/@([\w-]+)\s$/)); 

                if (match) {
                  fetchAndshowOrganizationDropdown(match[1]);
                }
              }

              if (e.key === 'Escape') {
                setshowOrganizationDropdown(false);
                setMentionInfo(null);
              }
            });
            
            editor.ui.registry.addButton("asset_link", {
              text: "Insert Asset Link",
              onAction: () => {
                editor.plugins.variable.addVariable('asset.link');
                validateContent(editor.getContent({ format: 'text' }));
              }
            });
          }
        }}
        onChange={(e:any) => {
          const htmlContent = e.target.getContent({format: 'html'});
          const companyMention = htmlContent.includes('data-original-variable');
          if (companyMention) {
            onChange(htmlContent);
          } else {
            const template = decodeHTMLEntities(widgetManager.getRawTemplate(e.target.getContent({format: outputFormat})));
            onChange(template);
          }
        }}
      />
    </>
  )
}

export default DescriptionEditor